/**
 * EventSelect
 *
 * Reusable async-paginated dropdown for selecting upcoming calendar events or tasks.
 * Mirrors the FirmUserSelect pattern — wraps AsyncPaginate with the
 * loadUpcomingEventOptions loader and shared AsyncSelect styles.
 *
 * Data source: POST /list-events-v2 with is_upcoming_dropdown=true
 *
 * Multi-select behaviour:
 *  - Each option shows a checkbox to communicate multi-select affordance.
 *  - Selected items are shown as scrollable chips in the control.
 *  - The menu stays open while selecting (closeMenuOnSelect=false when isMulti).
 *  - Selected options remain visible in the list (hideSelectedOptions=false).
 *  - Search is disabled — the API does not support keyword filtering.
 *
 * @param {Object}            props
 * @param {Object|Array|null} props.value          - Selected value(s); full option object(s)
 * @param {Function}          props.onChange        - Change handler; receives full option object(s)
 * @param {string}            [props.placeholder]
 * @param {boolean}           [props.isClearable]
 * @param {boolean}           [props.isDisabled]
 * @param {boolean}           [props.isMulti]       - Enables multi-select mode (default: true)
 * @param {string}            [props.className]
 * @param {number}            [props.pageSize]      - Records fetched per page (default: 10)
 * @param {string|null}       [props.calendarType]  - 'event' | 'task' | null (both)
 * @param {string}            [props.timezone]      - IANA timezone passed to the API
 * @param {Object}            [props.styles]        - Custom react-select style overrides
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadUpcomingEventOptions } from 'hooks/queries/useUpcomingEventsDropdown';
import '../shared/AsyncSelect.scss';
import './EventSelect.scss';

// ── Custom Option: renders a checkbox alongside the label ─────────────────────

function CheckboxOption({ innerRef, innerProps, isSelected, isFocused, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
    >
      <span
        className={`event-select-checkbox${isSelected ? ' event-select-checkbox--checked' : ''}`}
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function EventSelect({
  value = null,
  onChange,
  placeholder = 'Select an event',
  isClearable = true,
  isDisabled = false,
  isMulti = true,
  className = '',
  pageSize = 10,
  calendarType = null,
  timezone = 'UTC',
  caseId = null,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const loadOptions = useMemo(
    () => (_search, loadedOptions, additional) =>
      loadUpcomingEventOptions(_search, loadedOptions, additional, { pageSize, calendarType, timezone, caseId }),
    [pageSize, calendarType, timezone, caseId]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
        maxHeight: '38px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }),
      valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }),
      multiValue: (base) => ({
        ...base,
        flexShrink: 0,
        minWidth: 'fit-content',
      }),
      option: (base) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        padding: '4px 8px',
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles,
    }),
    [styles]
  );

  const components = useMemo(() => ({ Option: CheckboxOption }), []);

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={false}
      isMulti={isMulti}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      className={`event-select-dropdown ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={components}
      additional={{ page: 1 }}
      debounceTimeout={0}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading...'}
      noOptionsMessage={() => 'No upcoming events found'}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

EventSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  calendarType: PropTypes.string,
  timezone: PropTypes.string,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default EventSelect;
